import { createAsyncThunk } from '@reduxjs/toolkit';
import { doc, onSnapshot } from 'firebase/firestore';
import { RootState } from '../store';
import { CONFIG } from '../../config/mainConfig';
import { db } from '../../firebase';
import { callAPI } from '../../services/api.service';
import { setBookData, setBookDataModified, setBookId, setlistPlansToSubscribe } from '../appSlice';
import { debounce } from '../../helper/Debounce';
import { isEqual } from '../../helper/helper';
import { HTTPStatusEnum } from '../../interfaces/log.interface';
import { allCurrencies, CurrencyEnum } from '../../interfaces/plan.interface';

const MAX_RETRIES = 5;
const BASE_RETRY_DELAY = 5000;
const FETCH_DATA_DELAY = 300;

export const monitorBookData = createAsyncThunk(
  'app/monitorBookData',
  async (_, { getState, dispatch }) => {
    const state: RootState = getState() as RootState;
    const { bookId, bookDataModified, isTitleModalOpen } = state.app;
  
    if (!bookId) {
      dispatch(setBookData(null));
      return;
    }
  
    let unsubscribe: (() => void) | null = null;
    let retryCount = 0;
  
    const scheduleReconnect = () => {
      if (retryCount >= MAX_RETRIES) {
        return;
      }
  
      retryCount++;
      setTimeout(connectToSnapshot, BASE_RETRY_DELAY);
    };
  
    const connectToSnapshot = () => {
      try {
        unsubscribe = onSnapshot(
          doc(db, CONFIG.FIREBASE_COLLECTIONS.BOOKS, bookId),
          debounce(async (docSnap: any) => {
            if (docSnap.exists()) {
              if (bookDataModified) {
                const response = await callAPI('api/book-update/remove-pdf-url', { bookId });
                if (response?.status === HTTPStatusEnum.OK) {
                  dispatch(setBookDataModified(false));
                }
              }
  
              if (!isTitleModalOpen && !isEqual(state.app.bookData, docSnap.data()) && state.app.bookId === bookId) {
                const updatedBookData = docSnap.data();
                dispatch(setBookData(updatedBookData));
              }
            } else {
              dispatch(setBookData(null));
              dispatch(setBookId(null));
            }
          }, FETCH_DATA_DELAY),
          (error) => {
            scheduleReconnect();
          }
        );
      } catch (error) {
        scheduleReconnect();
      }
    };
  
    connectToSnapshot();
  
    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }
);

// Async Thunk for fetching plans to subscribe
export const fetchPlansToSubscribe = createAsyncThunk(
  'plans/listToSubscribe',
  async (_, { getState, dispatch }) => {
    try {
      const state: RootState = getState() as RootState;
      const user = state.user.user;
      const currentMainURLQuery = state.app.currentMainURLQuery;
      const searchParams = new URLSearchParams(currentMainURLQuery);
      const currency = searchParams.get(CONFIG.HEADERS.CURRENCY);
      
      // Get user's country for currency-specific pricing
      const userCountry = user?.shippingCountry || user?.detectedCountry;
      const userCurrency = allCurrencies.includes(currency as CurrencyEnum) ? currency as CurrencyEnum : user?.detectedCurrency;
      
      // Build query parameters
      const queryParams = userCountry ? `?country=${userCountry}&currency=${userCurrency}` : '';
      
      const data = await callAPI(`plans/listToSubscribe${queryParams}`);
      dispatch(setlistPlansToSubscribe(data?.list));
    } catch (error: any) {
      dispatch(setlistPlansToSubscribe([]));
    }
  }
);